import { COMMENT_BODY_MAX_LENGTH } from '@stores/MediaComments';
import classNames from 'classnames';
import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import css from './CommentsPanel.module.scss';

type Props = {
  /**
   * Rendered above the field — the anchor line on the new-comment composer. Omitted by the
   * reply and edit composers, which have no anchor of their own to state: a reply points where
   * its comment does, and an edit keeps the anchor it already had.
   */
  header?: ReactNode;
  placeholder: string;
  submitLabel: string;
  /** Seeds the field. Only the edit composer passes one; it is read once, on mount. */
  initialValue?: string;
  autoFocus?: boolean;
  /**
   * The field took focus — every time, not just the first. The caller decides what a second
   * focus means; see `pinIfUnpinned` in `CommentsPanel`, where it deliberately means nothing.
   */
  onFocus?: () => void;
  /**
   * Returns whether the write was accepted. `false` leaves the text in the field so it can be
   * sent again — which is the whole reason this is a boolean rather than a rejection: the write
   * has already been reported to the reader by then (see `CommentsPanel`'s `report`), so a
   * throw would be an error nobody is left to handle, at every one of the four call sites.
   */
  onSubmit: (body: string) => Promise<boolean>;
  /** Offered as a Cancel button. Absent on the new-comment composer, which is always on screen. */
  onCancel?: () => void;
  /**
   * Take focus now, from outside — the selection bar's Comment button. Not `autoFocus`, which
   * only fires on mount: the panel may already be open when the button is pressed.
   *
   * One-shot: `onFocusRequestHandled` is how the owner clears it. Left set, it would focus the
   * field again every time the panel remounted — each tab switch back to Comments — and a focus
   * here pins the anchor, so an unasked one quietly stops the anchor following the playhead.
   */
  focusRequested?: boolean;
  onFocusRequestHandled?: () => void;
  /** A key that focuses this field from elsewhere, announced as `aria-keyshortcuts`. */
  keyShortcut?: string;
};

/**
 * The one place text is written in this panel: a new comment, a reply, and an edit of either.
 *
 * All three want the same behaviour — submit on ⌘/Ctrl+Enter, refuse to send whitespace, stay
 * disabled and keep the text while the write is in flight, and stop at the API's body limit —
 * and the three of them drifting is how "your reply was silently truncated" ships. The caller
 * supplies only what differs: the labels, what sits above the field, and what to do with the
 * text.
 *
 * The field empties itself once a write is accepted, and only then. The new-comment composer is
 * the one that needs it — it stays on screen from one note to the next, where the reply and edit
 * boxes are unmounted by their callers.
 */
export const CommentComposer = ({
  header,
  placeholder,
  submitLabel,
  initialValue = '',
  autoFocus = false,
  onFocus,
  onSubmit,
  onCancel,
  focusRequested = false,
  onFocusRequestHandled,
  keyShortcut,
}: Props) => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grows with what is written instead of scrolling a three-line box. Reset to `auto` first,
  // or the height only ever ratchets up as text is deleted.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  useEffect(() => {
    if (!focusRequested) return;
    textareaRef.current?.focus();
    onFocusRequestHandled?.();
  }, [focusRequested, onFocusRequestHandled]);

  const canSubmit = value.trim().length > 0 && !submitting;

  const submit = useCallback(async () => {
    const body = value.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      if (await onSubmit(body)) {
        setValue('');
      }
    } finally {
      // `finally`, so a write that failed still hands the button back — the writer's text is
      // still in front of them and the next press is how they retry it.
      setSubmitting(false);
    }
  }, [value, submitting, onSubmit]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
      return;
    }
    // Escape backs out of a reply or an edit. Plain Enter deliberately does not send — these
    // are notes, and a note that wants a second paragraph should not have to fight for one.
    if (event.key === 'Escape' && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className={css.composer}>
      {header}
      <textarea
        ref={textareaRef}
        className={css.composerField}
        value={value}
        placeholder={placeholder}
        maxLength={COMMENT_BODY_MAX_LENGTH}
        rows={1}
        autoFocus={autoFocus}
        aria-keyshortcuts={keyShortcut}
        onFocus={onFocus}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div className={css.composerActions}>
        {onCancel && (
          <button type="button" className={css.secondaryButton} onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className={classNames(css.primaryButton, { [css.primaryButtonBusy]: submitting })}
          disabled={!canSubmit}
          onClick={() => void submit()}
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
};
